import { zodResolver } from '@hookform/resolvers/zod';
import { useCallback, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { useApiClient } from '@eluve/api-client-provider';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
  HStack,
  Input,
  NewButton,
  Text,
  VStack,
  useToast,
} from '@eluve/components';
import { useNamedLogger } from '@eluve/logger';

const passwordFormSchema = z.object({
  password: z.string().min(1, 'Password is required'),
});

type PasswordForm = z.infer<typeof passwordFormSchema>;

export interface PdfPasswordModalProps {
  isOpen: boolean;
  fileId: string | null;
  fileName: string | null;
  tenantId: string;
  threadId: string;
  onClose: () => void;
  onDecryptSuccess?: () => void;
}

export function PdfPasswordModal({
  isOpen,
  fileId,
  fileName,
  tenantId,
  threadId,
  onClose,
  onDecryptSuccess,
}: PdfPasswordModalProps) {
  const toast = useToast();
  const [isDecrypting, setIsDecrypting] = useState(false);
  const [passwordError, setPasswordError] = useState<string | null>(null);

  const logger = useNamedLogger('PdfPasswordModal');
  const apiClient = useApiClient();

  const form = useForm<PasswordForm>({
    defaultValues: { password: '' },
    resolver: zodResolver(passwordFormSchema),
  });

  const handleCancel = useCallback(() => {
    form.reset();
    setPasswordError(null);
    onClose();
  }, [form, onClose]);

  const decryptPdf = async (password: string) => {
    if (!fileId) {
      logger.error('No file selected for decryption');
      return;
    }

    try {
      setIsDecrypting(true);
      setPasswordError(null);

      const result = await apiClient.chat.decryptPdfFile({
        params: {
          tenantId,
          threadId,
          fileId,
        },
        body: {
          password,
        },
      });
      if (result.status === 200) {
        toast.success('PDF unlocked successfully');
        handleCancel();
        onDecryptSuccess?.();
      } else {
        setPasswordError('Invalid password');
      }
    } finally {
      setIsDecrypting(false);
    }
  };

  const handleSubmit = async (values: PasswordForm) => {
    await decryptPdf(values.password.trim());
  };

  const handleOpenChange = (open: boolean) => {
    if (!open && !isDecrypting) {
      handleCancel();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <Form {...form}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Password Required</DialogTitle>
          </DialogHeader>

          <form onSubmit={form.handleSubmit(handleSubmit)}>
            <VStack className="my-4" gap={4}>
              <VStack gap={2}>
                <Text size="sm" color="secondary">
                  Please enter password to unlock {fileName ?? 'file'}.
                </Text>
              </VStack>

              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem className="w-full">
                    <FormControl>
                      <Input
                        type="password"
                        placeholder="Enter password"
                        disabled={isDecrypting}
                        autoFocus
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              {passwordError && <FormMessage>{passwordError}</FormMessage>}
            </VStack>

            <DialogFooter>
              <HStack justify="end">
                <NewButton
                  text="Cancel"
                  type="outline"
                  size="m"
                  onClick={handleCancel}
                  disabled={isDecrypting}
                />
                <NewButton
                  text={isDecrypting ? 'Unlocking...' : 'Unlock'}
                  type="primary"
                  size="m"
                  submit
                  disabled={isDecrypting}
                />
              </HStack>
            </DialogFooter>
          </form>
        </DialogContent>
      </Form>
    </Dialog>
  );
}
